import React, { useState, useEffect } from 'react';
import { Text, TouchableOpacity, Modal, View, ActivityIndicator, Platform } from 'react-native';
import { Picker } from '@react-native-picker/picker';

import { updateBulkLocation } from '../../../../api/Product';
import { getBrandsData } from '../../../../api/Price';
import { createInventoryTransfer, getStoreLocationsData } from '../../../../api/Settings';
import BasicModalContainer from '../../../../common/components/basicmodal/BasicModalContainer';
import ModalHeader from '../../../../common/components/basicmodal/ModalHeader';
import ModalBody from '../../../../common/components/basicmodal/ModalBody';
import ModalFooter from '../../../../common/components/basicmodal/ModalFooter';
import { msgStr } from '../../../../common/constants/Message';
import { useAlertModal } from '../../../../common/hooks';

import { productModalstyles } from './styles/ProductModalStyle';
import { TextInput } from 'react-native';

const UpdateLocationModal = ({
  isModalVisible,
  selectedProducts,
  setUpdateProductsTrigger,
  closeModal,
  clearTableData,
}) => {
  const { showAlert } = useAlertModal();

  const [isLoading, setIsLoading] = useState(false);
  const [Locations, setLocations] = useState([]);
  const [Brands, setBrands] = useState([]);

  const [selectedBrand, selectBrand] = useState<number>(0);
  const [selectedBrandLocation, selectBrandLocation] = useState<number>(0);
  const [homeLocation, selectHomeLocation] = useState<string>('');
  const [currentLocation, selectCurrentLocation] = useState<string>('');

  // Banner state variables
  const [showBanner, setShowBanner] = useState(false);
  const [bannerType, setBannerType] = useState<'success' | 'error'>('success');
  const [bannerMessage, setBannerMessage] = useState('');
  const [bannerTimeout, setBannerTimeout] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isModalVisible) {
      if (!selectedProducts || !Array.isArray(selectedProducts) || selectedProducts.length == 0) {
        showAlert('warning', 'Please select product');
        closeModal();
      }

      getBrandsData((jsonRes, status) => {
        setBrands(jsonRes);
      });

      if (selectedProducts?.length === 1) {
        selectHomeLocation(selectedProducts?.at(0)?.home_location_tbl?.locations?.location || '');
        selectCurrentLocation(
          selectedProducts?.at(0)?.current_location_tbl?.locations?.location || ''
        );
      }
      setIsLoading(false);
    }
  }, [isModalVisible]);

  // Clear banner when modal closes
  useEffect(() => {
    if (!isModalVisible && bannerTimeout) {
      clearTimeout(bannerTimeout);
      setBannerTimeout(null);
      setShowBanner(false);
    }
  }, [isModalVisible]);

  // Function to show banner with auto-hide after 3 seconds
  const displayBanner = (type: 'success' | 'error', message: string) => {
    // Clear any existing timeout
    if (bannerTimeout) {
      clearTimeout(bannerTimeout);
    }

    // Set banner properties
    setBannerType(type);
    setBannerMessage(message);
    setShowBanner(true);

    // Auto-hide after 3 seconds
    const timeout = setTimeout(() => {
      setShowBanner(false);
    }, 3000);

    setBannerTimeout(timeout);
  };

  const UpdateLocationButtonHandler = (type: string) => {
    if (!selectedBrandLocation) {
      displayBanner('error', 'No location selected');
      return;
    }

    setIsLoading(true);

    const payload: any = {
      ids: selectedProducts.map((product) => product.id),
    };

    if (type === 'home') {
      payload.home_location = selectedBrandLocation;
    }
    if (type === 'current') {
      payload.current_location = selectedBrandLocation;
    }
    if (type === 'both') {
      payload.home_location = selectedBrandLocation;
      payload.current_location = selectedBrandLocation;
    }

    const handleResponse = (jsonRes, status) => {
      switch (status) {
        case 201:
          // Show success banner instead of alert
          displayBanner('success', jsonRes.message || 'Location updated successfully');

          const newLocationDetail = Locations.find(
            (location) => parseInt(location.id) === parseInt(String(selectedBrandLocation))
          );

          const payload = [];

          for (let i = 0; i < selectedProducts.length; i++) {
            const product = selectedProducts[i];

            if (type === 'home') {
              payload.push({
                barcode: product.barcode,
                old_location: product.home_location,
                new_location: selectedBrandLocation,
                item_id: product.id,
                display_name: product.family.display_name,
                location_type: 'home',
              });
              continue;
            }

            if (type === 'current') {
              payload.push({
                barcode: product.barcode,
                old_location: product.current_location,
                new_location: selectedBrandLocation,
                item_id: product.id,
                display_name: product.family.display_name,
                location_type: 'current',
              });
              continue;
            }

            if (type === 'both') {
              payload.push({
                barcode: product.barcode,
                old_location: product.home_location,
                new_location: selectedBrandLocation,
                item_id: product.id,
                display_name: product.family.display_name,
                location_type: 'home',
              });

              payload.push({
                barcode: product.barcode,
                old_location: product.current_location,
                new_location: selectedBrandLocation,
                item_id: product.id,
                display_name: product.family.display_name,
                location_type: 'current',
              });
              continue;
            }
          }

          createInventoryTransfer(payload, (jsonRes, status, error) => {
            // No need to show alert here
          });

          if (selectedProducts.length === 1) {
            if (type === 'home') {
              selectHomeLocation(newLocationDetail?.locations?.location || '');
            }
            if (type === 'current') {
              selectCurrentLocation(newLocationDetail?.locations?.location || '');
            }
            if (type === 'both') {
              selectHomeLocation(newLocationDetail?.locations?.location || '');
              selectCurrentLocation(newLocationDetail?.locations?.location || '');
              closeModal();
              clearTableData();
            }
          }

          setUpdateProductsTrigger(true);
          break;
        default:
          // Show error banner instead of alert
          if (jsonRes && jsonRes.error) {
            displayBanner('error', jsonRes.error);
          } else {
            displayBanner('error', msgStr('unknownError'));
          }
          break;
      }
      setIsLoading(false);
    };

    updateBulkLocation(payload, (jsonRes, status) => {
      handleResponse(jsonRes, status);
    });
  };

  const handleBrandChange = async (brandId: number) => {
    if (!brandId) return;
    selectBrand(brandId);

    await getStoreLocationsData(brandId, (jsonRes, status, error) => {
      if (status == 200) {
        setLocations(jsonRes.storeLocations);
      } else {
        setLocations([]);
      }
    });
  };

  return (
    <Modal animationType="none" transparent={true} visible={isModalVisible} onShow={() => {}}>
      <BasicModalContainer>
        <ModalHeader label={'Update location'} closeModal={closeModal} />
        {showBanner && (
          <View
            style={[
              styles.banner,
              bannerType === 'success' ? styles.successBanner : styles.errorBanner,
            ]}
          >
            <Text
              style={[
                styles.bannerText,
                bannerType === 'success' ? styles.successText : styles.errorText,
              ]}
            >
              {bannerMessage}
            </Text>
          </View>
        )}
        <ModalBody>
          <View style={{ flexDirection: 'row' }}>
            <View style={{ flex: 1 }}>
              <Text style={styles.label}>Brands</Text>
              <Picker
                style={styles.select}
                selectedValue={selectedBrand}
                onValueChange={(itemValue, itemIndex) => {
                  handleBrandChange(itemValue);
                }}
              >
                <Picker.Item label={''} value={0} />
                {Brands.length > 0 &&
                  Brands.map((brand, index) => {
                    return <Picker.Item key={index} label={brand.brand} value={brand.id} />;
                  })}
              </Picker>

              <Text style={styles.label}>Select Location</Text>
              <Picker
                style={styles.select}
                selectedValue={selectedBrandLocation}
                onValueChange={(itemValue, itemIndex) => {
                  selectBrandLocation(itemValue);
                }}
              >
                <Picker.Item label={''} value={0} />
                {Locations.length > 0 &&
                  Locations.map((location, index) => {
                    return (
                      <Picker.Item
                        key={index}
                        label={location.locations.location}
                        value={location.id}
                      />
                    );
                  })}
              </Picker>

              <View
                style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 10 }}
              >
                <TouchableOpacity onPress={() => UpdateLocationButtonHandler('home')}>
                  <Text style={styles.addButton}>{'Set Home Location'}</Text>
                </TouchableOpacity>

                <TouchableOpacity onPress={() => UpdateLocationButtonHandler('current')}>
                  <Text style={styles.addButton}>{'Set Current Location'}</Text>
                </TouchableOpacity>

                <TouchableOpacity onPress={() => UpdateLocationButtonHandler('both')}>
                  <Text style={styles.addButton}>{'Set Both'}</Text>
                </TouchableOpacity>
              </View>

              <View style={{ display: selectedProducts.length > 1 ? 'none' : 'flex' }}>
                <Text style={styles.label}>Home Location</Text>
                <TextInput
                  style={styles.input}
                  placeholder=""
                  editable={false}
                  value={homeLocation}
                />

                <Text style={styles.label}>Current Location</Text>
                <TextInput
                  style={styles.input}
                  placeholder=""
                  editable={false}
                  value={currentLocation}
                />
              </View>
            </View>
          </View>
        </ModalBody>
        {/* <ModalFooter>
          <TouchableOpacity onPress={UpdateLocationButtonHandler}>
            <Text style={styles.addButton}>{'Update'}</Text>
          </TouchableOpacity>
        </ModalFooter> */}
      </BasicModalContainer>
      {isLoading && (
        <View style={styles.overlay}>
          <ActivityIndicator size="large" color="#0000ff" />
        </View>
      )}
    </Modal>
  );
};

const styles = productModalstyles;

export default UpdateLocationModal;
